if (!customElements.get('bp-discount-label')) {
  /**
   * Bathware Plus: shows the title of the automatic discount Shopify applies to a variant.
   * Liquid can't see automatic discounts before the cart, so this creates a throwaway
   * Storefront API cart (not the customer's cart) holding the variant and reads its discounts.
   * Labels with data-batch (product cards) share one cart per batch of up to 50 variants.
   */
  class BpDiscountLabel extends HTMLElement {
    connectedCallback() {
      this.load();
    }

    async load() {
      const { variantId, country } = this.dataset;
      const title = this.hasAttribute('data-batch')
        ? await BpDiscountLabel.getBatchedTitle(variantId, country)
        : await BpDiscountLabel.getTitle(variantId, country);
      if (!title || !this.isConnected) return;

      this.querySelector('.js-discount-title').textContent = title;
      this.hidden = false;
    }

    static getTitle(variantId, country) {
      return BpDiscountLabel.remember(`single:${variantId}`, () =>
        BpDiscountLabel.fetchTitles([variantId], country).then((titles) => titles[variantId] || '')
      );
    }

    static getBatchedTitle(variantId, country) {
      return BpDiscountLabel.remember(`batch:${variantId}`, () => new Promise((resolve) => {
        BpDiscountLabel.queue.push({ variantId, country, resolve });
        clearTimeout(BpDiscountLabel.flushTimer);
        BpDiscountLabel.flushTimer = setTimeout(BpDiscountLabel.flush, 50);
      }));
    }

    static remember(key, request) {
      if (!BpDiscountLabel.requests[key]) BpDiscountLabel.requests[key] = request();
      return BpDiscountLabel.requests[key];
    }

    static flush() {
      const queue = BpDiscountLabel.queue;
      BpDiscountLabel.queue = [];

      for (let i = 0; i < queue.length; i += BpDiscountLabel.batchSize) {
        const batch = queue.slice(i, i + BpDiscountLabel.batchSize);
        BpDiscountLabel.fetchTitles(batch.map((item) => item.variantId), batch[0].country).then((titles) => {
          batch.forEach((item) => item.resolve(titles[item.variantId] || ''));
        });
      }
    }

    static async fetchTitles(variantIds, country) {
      try {
        const response = await fetch(`/api/${BpDiscountLabel.apiVersion}/graphql.json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: BpDiscountLabel.query,
            variables: {
              count: variantIds.length,
              input: {
                lines: variantIds.map((id) => ({ merchandiseId: `gid://shopify/ProductVariant/${id}`, quantity: 1 })),
                buyerIdentity: country ? { countryCode: country } : null
              }
            }
          })
        });
        const json = await response.json();
        const titles = {};
        json.data.cartCreate.cart.lines.nodes.forEach((line) => {
          const lineTitles = line.discountAllocations.map((allocation) => allocation.title).filter(Boolean);
          titles[line.merchandise.id.split('/').pop()] = [...new Set(lineTitles)].join(', ');
        });
        return titles;
      } catch (e) {
        return {};
      }
    }
  }

  BpDiscountLabel.apiVersion = '2026-10';
  BpDiscountLabel.batchSize = 50;
  BpDiscountLabel.requests = {};
  BpDiscountLabel.queue = [];
  BpDiscountLabel.query = `
    mutation BpDiscountLabel($input: CartInput!, $count: Int!) {
      cartCreate(input: $input) {
        cart {
          lines(first: $count) {
            nodes {
              merchandise {
                ... on ProductVariant {
                  id
                }
              }
              discountAllocations {
                ... on CartAutomaticDiscountAllocation {
                  title
                }
              }
            }
          }
        }
      }
    }
  `;

  customElements.define('bp-discount-label', BpDiscountLabel);
}
